import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Minimize2, Maximize2, Send, Bot, User } from 'lucide-react';
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: 'Bonjour ! Je suis votre assistant virtuel HSA.\n\nJe peux vous aider avec :\n\n• L\'import automobile\n• L\'homologation européenne\n• Le sourcing international\n• Nos services premium\n\nPosez-moi votre question !',
      timestamp: new Date(),
      suggestions: ['Import Porsche 911', 'Homologation européenne', 'Zones d\'intervention', 'Services HSA']
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [typingIndicator, setTypingIndicator] = useState(false);
  const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatContainerRef = useRef(null);
  const chatRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Detect mobile screen size
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Prevent body scroll when chatbot is open on mobile
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isMobile]);

  // Welcome animation sequence
  useEffect(() => {
    if (showWelcomeAnimation) {
      const timer = setTimeout(() => {
        setShowWelcomeAnimation(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showWelcomeAnimation]);

  // Auto-acid-green effect for bot messages
  useEffect(() => {
    if (typingIndicator) {
      const timer = setTimeout(() => {
        setTypingIndicator(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [typingIndicator]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShowSuggestions(false);

    // Simulate typing indicator
    setTimeout(() => setTypingIndicator(true), 500);

    try {
      // Check if OpenAI API key is configured
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      
      if (!apiKey || apiKey === 'your_openai_api_key_here') {
        throw new Error('OpenAI API key not configured');
      }

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: "gpt-4",
          messages: [
            {
              role: "system",
              content: "Tu es l'assistant virtuel de HSA (HIGH SELECTION AUTO), une entreprise spécialisée dans l'import de véhicules de prestige en France.\n\n" +
                "CONTEXTE HSA :\n" +
                "- Entreprise française d'import automobile premium\n" +
                "- Spécialisée dans les véhicules de luxe et sport\n" +
                "- Services : sourcing, homologation, logistique, accompagnement\n" +
                "- Marchés cibles : Japon, Allemagne, Italie, USA, UK\n" +
                "- Marques : Porsche, Ferrari, Lamborghini, Nissan GT-R, BMW M, Mercedes-AMG, etc.\n" +
                "- Zones d'intervention : Île-de-France, PACA, Occitanie, Auvergne-Rhône-Alpes\n\n" +
                "TON RÔLE :\n" +
                "- Répondre aux questions sur les services HSA\n" +
                "- Expliquer le processus d'import\n" +
                "- Donner des informations sur l'homologation\n" +
                "- Orienter vers les bonnes démarches\n" +
                "- Être professionnel, rassurant et expert\n" +
                "- Toujours rester dans le domaine automobile et HSA\n\n" +
                "RÈGLES :\n" +
                "- Réponses en français, courtoises et professionnelles\n" +
                "- Maximum 3-4 phrases par réponse\n" +
                "- Orienter vers le contact HSA si question complexe\n" +
                "- Ne jamais donner de prix ou délais précis\n" +
                "- Toujours mentionner l'expertise HSA\n" +
                "- Utiliser des emojis appropriés pour rendre les réponses plus engageantes"
            },
            {
              role: "user",
              content: userMessage.content
            }
          ],
          max_tokens: 300,
          temperature: 0.7,
          presence_penalty: 0.1,
          frequency_penalty: 0.1,
        })
      });

      if (response.ok) {
        const data = await response.json();
        const botMessage = {
          id: Date.now() + 1,
          type: 'bot',
          content: data.choices[0].message.content,
          timestamp: new Date(),
          suggestions: ['Autre question', 'Contact HSA', 'Nos services', 'Tarifs']
        };
        
        setMessages(prev => [...prev, botMessage]);
        setShowSuggestions(true);
      } else {
        throw new Error('Erreur API');
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: 'Désolé, je rencontre un problème technique. Veuillez réessayer ou contacter directement l\'équipe HSA. 📞',
        timestamp: new Date(),
        suggestions: ['Réessayer', 'Contact HSA', 'Nos services']
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setTypingIndicator(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    inputRef.current?.focus();
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
    setIsExpanded(false);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      {/* Floating Action Button - Luxe et épuré */}
      <motion.div
        className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-[9999]"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        style={{ pointerEvents: 'auto' }}
      >
        <motion.button
          onClick={toggleChat}
          className="w-16 h-16 bg-gradient-to-br from-acid-green to-acid-green/80 rounded-full shadow-2xl shadow-acid-green/30 flex items-center justify-center text-white hover:shadow-acid-green/50 transition-all duration-300 group relative overflow-hidden"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label={isOpen ? "Fermer le chat" : "Ouvrir le chat"}
        >
          {/* Subtle background pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <X className="w-6 h-6 text-white" />
              </motion.div>
            ) : (
              <motion.div
                key="chat"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <MessageCircle className="w-6 h-6 text-white" />
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Subtle pulse effect */}
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-white/20"
            animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.button>
      </motion.div>

      {/* Mobile Backdrop Overlay */}
      <AnimatePresence>
        {isOpen && isMobile && (
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9998]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={toggleChat}
          />
        )}
      </AnimatePresence>

      {/* Chat Interface - Design luxe et épuré */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={chatRef}
            className="fixed z-[9999]"
            style={{ 
              bottom: isMobile ? '0' : '5rem',
              left: isMobile ? '0' : '1.5rem',
              width: isMobile ? '100%' : (isExpanded ? '500px' : '400px'),
              maxWidth: isMobile ? '100%' : '500px',
              maxHeight: isMobile ? '85vh' : 'none',
            }}
            initial={{ opacity: 0, y: isMobile ? '100%' : 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: isMobile ? '100%' : 100, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Chat Container */}
            <motion.div 
              className={`bg-white rounded-t-3xl ${isMobile ? 'rounded-b-none' : 'rounded-3xl'} shadow-2xl shadow-acid-green/20 border border-acid-green/20 overflow-hidden relative flex flex-col`}
              style={{ 
                height: isMobile ? '85vh' : (
                  isMinimized ? '80px' : (isExpanded ? '700px' : '600px')
                ),
              }}
            >
              {/* Header épuré */}
              <motion.div 
                className={`bg-gradient-to-r from-acid-green to-acid-green/90 relative flex-shrink-0 ${isMobile ? 'p-3' : 'p-4'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 ${isMobile ? 'w-8 h-8' : 'w-10 h-10'}`}>
                      <Bot className={`text-white ${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
                    </div>
                    <div>
                      <h3 className={`font-bold text-white ${isMobile ? 'text-base' : 'text-lg'}`}>
                        Assistant HSA
                      </h3>
                      <div className="flex items-center space-x-2">
                        <div className={`rounded-full ${isOnline ? 'bg-green-400' : 'bg-red-400'} ${isMobile ? 'w-1.5 h-1.5' : 'w-2 h-2'}`} />
                        <p className={`text-white/90 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          {isOnline ? 'En ligne' : 'Hors ligne'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Contrôles simplifiés */}
                  <div className="flex items-center space-x-2">
                    {!isMobile && (
                      <motion.button
                        onClick={toggleExpand}
                        className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 text-white hover:bg-white/30 transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title={isExpanded ? "Réduire" : "Agrandir"}
                      >
                        {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                      </motion.button>
                    )}
                    
                    <motion.button
                      onClick={toggleMinimize}
                      className={`bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30 text-white hover:bg-white/30 transition-colors ${isMobile ? 'w-7 h-7' : 'w-8 h-8'}`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title={isMinimized ? "Développer" : "Minimiser"}
                    >
                      <Minimize2 className={isMobile ? "w-3.5 h-3.5" : "w-4 h-4"} />
                    </motion.button>
                  </div>
                </div>
              </motion.div>

              {/* Messages Container */}
              <AnimatePresence>
                {!isMinimized && (
                  <motion.div 
                    className={`flex-1 overflow-y-auto bg-gray-50 ${isMobile ? 'p-2' : 'p-4'}`}
                    style={{ 
                      maxHeight: isMobile 
                        ? 'calc(85vh - 180px)' 
                        : `calc(${isExpanded ? '700px' : '600px'} - 200px)`,
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className={`${isMobile ? 'space-y-2' : 'space-y-4'}`} ref={chatContainerRef}>
                      {messages.map((message, index) => (
                        <motion.div
                          key={message.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`flex items-start max-w-[90%] ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''} ${isMobile ? 'space-x-2' : 'space-x-3'}`}>
                            {/* Avatar simplifié */}
                            <div className={`rounded-full flex items-center justify-center flex-shrink-0 ${
                              message.type === 'user' 
                                ? 'bg-acid-green text-white' 
                                : 'bg-gray-200 text-gray-600'
                            } ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`}>
                              {message.type === 'user' ? (
                                <User className={isMobile ? "w-3 h-3" : "w-4 h-4"} />
                              ) : (
                                <Bot className={isMobile ? "w-3 h-3" : "w-4 h-4"} />
                              )}
                            </div>
                            
                            {/* Message Bubble épuré */}
                            <div className={`shadow-sm ${
                              message.type === 'user'
                                ? 'bg-acid-green text-white'
                                : 'bg-white text-gray-800 border border-gray-200'
                            } ${isMobile ? 'rounded-xl px-3 py-2' : 'rounded-2xl px-4 py-3'}`}>
                              <div className={`leading-relaxed whitespace-pre-line ${isMobile ? 'text-xs' : 'text-sm'}`}>{message.content}</div>
                              
                              {/* Footer simplifié */}
                              <div className={`flex items-center justify-between ${isMobile ? 'mt-1 text-xs' : 'mt-2 text-xs'} ${
                                message.type === 'user' ? 'text-white/70' : 'text-gray-500'
                              }`}>
                                <span>{formatTime(message.timestamp)}</span>
                                {message.type === 'bot' && (
                                  <span className="text-acid-green font-medium">HSA</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                      
                      {/* Indicateur de frappe simplifié */}
                      {typingIndicator && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex justify-start"
                        >
                          <div className={`flex items-start ${isMobile ? 'space-x-2' : 'space-x-3'}`}>
                            <div className={`bg-gray-200 rounded-full flex items-center justify-center ${isMobile ? 'w-6 h-6' : 'w-8 h-8'}`}>
                              <Bot className={`text-gray-600 ${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                            </div>
                            <div className={`bg-white shadow-sm border border-gray-200 ${isMobile ? 'rounded-xl px-3 py-2' : 'rounded-2xl px-4 py-3'}`}>
                              <div className="flex items-center space-x-2">
                                <div className="flex space-x-1">
                                  <motion.div
                                    className={`bg-acid-green rounded-full ${isMobile ? 'w-1.5 h-1.5' : 'w-2 h-2'}`}
                                    animate={{ scale: [1, 1.2, 1] }}
                                    transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                                  />
                                  <motion.div
                                    className={`bg-acid-green rounded-full ${isMobile ? 'w-1.5 h-1.5' : 'w-2 h-2'}`}
                                    animate={{ scale: [1, 1.2, 1] }}
                                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                                  />
                                  <motion.div
                                    className={`bg-acid-green rounded-full ${isMobile ? 'w-1.5 h-1.5' : 'w-2 h-2'}`}
                                    animate={{ scale: [1, 1.2, 1] }}
                                    transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                                  />
                                </div>
                                <span className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>HSA réfléchit...</span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                      
                      {/* Suggestions simplifiées */}
                      {showSuggestions && messages.length > 1 && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex flex-wrap gap-2 justify-center"
                        >
                          {messages[messages.length - 1].suggestions?.map((suggestion, index) => (
                            <motion.button
                              key={index}
                              onClick={() => handleSuggestionClick(suggestion)}
                              className={`bg-white rounded-full text-gray-700 border border-gray-200 hover:bg-acid-green hover:text-white hover:border-acid-green transition-all duration-200 ${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'}`}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.1 }}
                            >
                              {suggestion}
                            </motion.button>
                          ))}
                        </motion.div>
                      )}
                      
                      <div ref={messagesEndRef} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Zone de saisie épurée */}
              <AnimatePresence>
                {!isMinimized && (
                  <motion.div 
                    className={`bg-white border-t border-gray-200 flex-shrink-0 ${isMobile ? 'p-2' : 'p-4'}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <form onSubmit={handleSubmit} className={isMobile ? "space-y-2" : "space-y-3"}>
                      <div className={`flex ${isMobile ? 'space-x-2' : 'space-x-3'}`}>
                        <div className="flex-1 relative">
                          <input
                            ref={inputRef}
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder={isMobile ? "Votre question..." : "Posez votre question sur l'import automobile..."}
                            className={`w-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-acid-green/30 focus:border-acid-green transition-all duration-300 bg-white placeholder-gray-500 text-gray-800 ${isMobile ? 'px-3 py-2 rounded-xl text-sm' : 'px-4 py-3 rounded-2xl'}`}
                            disabled={isLoading}
                          />
                        </div>
                        
                        {/* Bouton d'envoi simplifié */}
                        <motion.button
                          type="submit"
                          disabled={!inputValue.trim() || isLoading}
                          className={`bg-acid-green flex items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-acid-green/90 transition-all duration-300 flex-shrink-0 ${isMobile ? 'w-10 h-10 rounded-xl' : 'w-12 h-12 rounded-2xl'}`}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Send className={isMobile ? "w-4 h-4" : "w-5 h-5"} />
                        </motion.button>
                      </div>
                      
                      {/* Footer simplifié */}
                      <div className="flex items-center justify-between">
                        <p className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-xs'}`}>
                          Assistant virtuel HSA
                        </p>
                        <div className="flex items-center space-x-2">
                          <div className={`bg-green-400 rounded-full ${isMobile ? 'w-1.5 h-1.5' : 'w-2 h-2'}`} />
                          
                        </div>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;
